import { useMemo } from "react";
import { useTeamMembers, type TeamMember } from "./api";
import { useWorkspace } from "../workspace/WorkspaceContext";

export interface MembersIndex {
  byId: Map<string, TeamMember>;
  list: TeamMember[];
  loading: boolean;
  resolveName: (id: string | null | undefined) => string;
}

export function useMembersIndex(): MembersIndex {
  const { current } = useWorkspace();
  // Include soft-deleted users so assignees on old leads / transfers /
  // activities still resolve to a human-readable name instead of leaking
  // their raw UUID.
  const { data, isLoading } = useTeamMembers(current?.id, { includeDeleted: true });
  const list = data?.users ?? [];
  const byId = useMemo(() => {
    const m = new Map<string, TeamMember>();
    list.forEach((u) => m.set(u.id, u));
    return m;
  }, [list]);
  const resolveName = (id: string | null | undefined) => {
    if (!id) return "Unassigned";
    const member = byId.get(id);
    if (!member) return isLoading ? "…" : "Unknown user";
    return member.deletedAt ? `${member.name} (removed)` : member.name;
  };
  return { byId, list, loading: isLoading, resolveName };
}
